/**
 * Progressively enhances pagination with manual loading or infinite scrolling.
 * The next-page link remains usable when JavaScript is unavailable.
 */
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    static get observedAttributes() {
      return ['data-pause-infinite-scroll'];
    }

    constructor() {
      super();
      this.requestVersion = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handleRestore = this.reload.bind(this);
    }

    connectedCallback() {
      if (!this.initialized) this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    attributeChangedCallback(name, previousValue, value) {
      if (!this.initialized || previousValue === value) return;
      if (value === 'true') {
        this.cancelRequest();
        this.paginationObserver?.disconnect();
      } else {
        this.observePagination();
      }
    }

    init() {
      this.initialized = true;
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.section = this.closest('.shopify-section[id]');
      this.wrapperIndex = Array.from((this.section || document).querySelectorAll('custom-pagination')).indexOf(this);
      this.loadedUrls = new Set([this.pageKey(window.location.href)]);
      this.failed = false;
      this.isFetching = false;
      this.controller = null;
      this.updateElements();
      this.addEventListener('click', this.handleClick);
      document.addEventListener('on:bfcache:load-restore', this.handleRestore);

      if (this.infiniteScroll && typeof IntersectionObserver !== 'undefined') {
        this.paginationObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.target === this.pagination && entry.isIntersecting)) {
            this.loadMore();
          }
        }, { rootMargin: `${Math.min(960, Math.max(480, window.innerHeight))}px 0px` });
      }
      this.observePagination();
    }

    updateElements() {
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.pagination?.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.resultsContainer = this.results[0]?.parentElement
        || this.querySelector('#filter-results .grid, .js-pagination-results, .grid');
      if (!this.infiniteScroll || typeof IntersectionObserver !== 'undefined') {
        this.pagination?.classList.add('is-enhanced');
      }
      // Section rendering is an internal fetch optimization, never a navigation target.
      if (this.loadMoreButton?.getAttribute('href')) {
        const nextUrl = new URL(this.loadMoreButton.getAttribute('href'), window.location.href);
        nextUrl.searchParams.delete('section_id');
        this.loadMoreButton.href = nextUrl.href;
      }
    }

    hasMore() {
      return this.pagination?.dataset.isMoreResults === 'true'
        && !!this.loadMoreButton?.getAttribute('href');
    }

    observePagination() {
      if (this.hasMore() && !this.isFetching && !this.failed && this.dataset.pauseInfiniteScroll !== 'true') {
        this.paginationObserver?.observe(this.pagination);
      }
    }

    cancelRequest() {
      clearTimeout(this.requestTimeout);
      this.requestTimeout = null;
      this.requestVersion += 1;
      this.controller?.abort();
      this.controller = null;
      this.isFetching = false;
      this.setLoading(false);
    }

    destroy() {
      this.cancelRequest();
      this.paginationObserver?.disconnect();
      this.paginationObserver = null;
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('on:bfcache:load-restore', this.handleRestore);
      this.initialized = false;
    }

    /** Called by both facet controllers after replacing the result markup. */
    reload() {
      this.destroy();
      if (this.isConnected) this.init();
    }

    handleClick(event) {
      const control = event.target.closest('.js-pagination-load-more, .js-pagination-retry');
      if (control && control.closest('custom-pagination') === this) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        this.loadMore(true);
      } else if (event.target.closest('.js-pagination-result')) {
        const link = event.target.closest('a');
        if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey
          || event.altKey || event.button > 0 || (link.target && link.target !== '_self')
          || link.hasAttribute('download')) return;
        this.cancelRequest();
        this.paginationObserver?.disconnect();
      }
    }

    setLoading(loading) {
      if (!this.pagination) return;
      const showPaginationSpinner = this.infiniteScroll
        || this.pagination.dataset.paginationStyle === 'traditional';
      this.pagination.classList.toggle('is-loading', loading && showPaginationSpinner);
      this.pagination.setAttribute('aria-busy', String(loading));
      this.loadMoreButton?.classList.toggle('is-loading', loading);
      if (loading) this.loadMoreButton?.setAttribute('aria-disabled', 'true');
      else this.loadMoreButton?.removeAttribute('aria-disabled');
      const indicator = this.pagination.querySelector('.js-pagination-loading');
      if (indicator) indicator.hidden = !loading;
      const retry = this.pagination.querySelector('.js-pagination-retry');
      if (retry) retry.disabled = loading;
    }

    setError(failed) {
      this.failed = failed;
      this.pagination?.classList.toggle('has-error', failed);
      const error = this.pagination?.querySelector('.js-pagination-error');
      if (error) error.hidden = !failed;
    }

    /** Keep comparisons independent of fragment anchors and section rendering. */
    pageKey(url) {
      const page = new URL(url, window.location.href);
      page.hash = '';
      page.searchParams.delete('section_id');
      if (page.searchParams.get('page') === '1') page.searchParams.delete('page');
      page.searchParams.sort();
      return page.href;
    }

    responseWrapper(content) {
      let scope = content;
      if (this.section) {
        scope = content.getElementById(this.section.id);
        // Shopify can return a bare section, but a different section is never a match.
        if (!scope && content.querySelector('.shopify-section')) return null;
        scope = scope || content;
      }
      const wrappers = Array.from(scope.querySelectorAll('custom-pagination'));
      return this.id ? wrappers.find((wrapper) => wrapper.id === this.id) : wrappers[this.wrapperIndex];
    }

    announce(message, complete = false) {
      if (!message || !this.pagination) return;
      let status = this.pagination.querySelector('.js-pagination-status');
      if (!status) {
        status = document.createElement('span');
        status.className = 'js-pagination-status visually-hidden';
        status.setAttribute('role', 'status');
        this.pagination.append(status);
      }
      status.textContent = message;
      if (complete) {
        const wrapper = this.pagination.closest('nav');
        (wrapper && this.contains(wrapper) ? wrapper : this.pagination).before(status);
      }
    }

    async loadMore(userInitiated = false) {
      if (!this.initialized || this.isFetching || !this.hasMore() || this.dataset.pauseInfiniteScroll === 'true'
        || (this.failed && !userInitiated)) return;

      this.isFetching = true;
      this.setError(false);
      this.paginationObserver?.unobserve(this.pagination);
      this.setLoading(true);
      const version = ++this.requestVersion;
      const controller = new AbortController();
      this.controller = controller;
      const pageUrl = new URL(this.loadMoreButton.href, window.location.href);
      const pageKey = this.pageKey(pageUrl);
      if (this.section) pageUrl.searchParams.set('section_id', this.section.id.replace(/^shopify-section-/, ''));
      const isCurrent = () => version === this.requestVersion && !controller.signal.aborted
        && this.isConnected && this.dataset.pauseInfiniteScroll !== 'true';

      const requestTimeout = setTimeout(() => {
        if (!isCurrent()) return;
        this.cancelRequest();
        this.setError(true);
      }, 20000);
      this.requestTimeout = requestTimeout;

      try {
        const response = await fetch(pageUrl.href, { signal: controller.signal });
        if (!response.ok) throw new Error(`Pagination request failed: ${response.status}`);
        const html = await response.text();
        // A filter or navigation can interrupt the request while its body is being read.
        if (!isCurrent()) return;
        const template = document.createElement('template');
        template.innerHTML = html;
        const nextWrapper = this.responseWrapper(template.content);
        if (!nextWrapper || !this.resultsContainer) throw new Error('Incomplete pagination response');
        const newResults = Array.from(nextWrapper.querySelectorAll('.js-pagination-result'));
        const nextPagination = nextWrapper.querySelector('.js-pagination');
        const nextLink = nextPagination?.querySelector('.js-pagination-load-more');
        const moreResults = nextPagination?.dataset.isMoreResults === 'true';
        if (moreResults) {
          if (!nextLink?.getAttribute('href')) throw new Error('Missing next page link');
          const nextKey = this.pageKey(nextLink.getAttribute('href'));
          if (nextKey === pageKey || this.loadedUrls.has(nextKey)) throw new Error('Repeated next page link');
        }
        if (!isCurrent()) return;
        this.resultsContainer.append(...newResults);
        this.loadedUrls.add(pageKey);
        const loadedMessage = this.pagination.dataset.loadedMessage;
        const completeMessage = this.pagination.dataset.completeMessage;
        if (moreResults) {
          this.pagination.replaceWith(nextPagination);
          this.updateElements();
          this.announce(loadedMessage);
        } else {
          this.announce(completeMessage || loadedMessage, true);
          const nav = this.pagination.closest('nav');
          (nav && this.contains(nav) ? nav : this.pagination).remove();
          this.paginationObserver?.disconnect();
          this.querySelectorAll('.js-when-paginated-only').forEach((element) => element.remove());
          this.updateElements();
        }
        // Only user-requested loads move focus; automatic loading never interrupts reading.
        if (userInitiated) newResults[0]?.querySelector('a, button')?.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('on:pagination:updated', { bubbles: true }));
      } catch (error) {
        if (isCurrent() && error.name !== 'AbortError') this.setError(true);
      } finally {
        clearTimeout(requestTimeout);
        if (version === this.requestVersion) {
          this.requestTimeout = null;
          this.controller = null;
          this.isFetching = false;
          this.setLoading(false);
          // Re-observing after an append also fills short grids without polling.
          if (!controller.signal.aborted) this.observePagination();
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
